import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useFacilityModeData } from './FacilityModeDataContext';
import { IAttainsWatershed } from '../../../types/attains';
import { fetchDmrCrosswalk } from '../../../utils/attainsApi';
import {
  matchDischargeToImpairments,
  IFacilityParam,
} from '../../../utils/dischargeImpairmentMatch';

// ─── styling (mirrors the DataCard visual language, wider for multi-line facts) ──

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Card = styled.article<{ $unavailable?: boolean }>`
  flex: 0 0 320px;
  max-height: 140px;
  overflow-y: auto;
  background: ${({ $unavailable }) =>
    $unavailable ? 'rgba(30, 32, 44, 0.88)' : 'rgba(13, 20, 38, 0.9)'};
  border: 1px solid
    ${({ $unavailable }) => ($unavailable ? 'rgba(255,255,255,0.07)' : 'rgba(52,211,153,0.18)')};
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  backdrop-filter: blur(8px);
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
  gap: 0.25rem;
`;

const SourcePill = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: #8896ac;
  font-family: 'DM Sans', -apple-system, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const CyclePill = styled.span`
  flex-shrink: 0;
  font-size: 0.6rem;
  border-radius: 999px;
  padding: 1px 6px;
  font-family: 'DM Sans', -apple-system, sans-serif;
  white-space: nowrap;
  cursor: help;
  background: rgba(100, 116, 139, 0.18);
  color: #94a3b8;
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
`;

const MaxBtn = styled.button`
  background: none;
  border: none;
  color: #8896ac;
  cursor: pointer;
  font-size: 1.05rem;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  &:hover {
    color: #34d399;
    background: rgba(52, 211, 153, 0.12);
  }
`;

const Line = styled.div`
  font-size: 0.68rem;
  color: #cbd5e1;
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1.4;
  margin-bottom: 0.25rem;
`;

const Muted = styled.span`
  color: #8896ac;
`;

const Impaired = styled.span`
  color: #f87171;
  font-weight: 600;
`;

const Supporting = styled.span`
  color: #34d399;
  font-weight: 600;
`;

const CauseRow = styled.div`
  font-size: 0.66rem;
  color: #e2e8f0;
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1.4;
`;

const CauseUses = styled.span`
  color: #8896ac;
`;

const Sub = styled.div`
  font-size: 0.58rem;
  color: rgba(136, 150, 172, 0.7);
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1.35;
  margin-top: 0.35rem;
`;

const XRefHead = styled.div`
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-family: 'DM Sans', -apple-system, sans-serif;
  margin-top: 0.45rem;
  padding-top: 0.35rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const XRefLink = styled.div`
  font-size: 0.64rem;
  color: #fbbf24;
  font-weight: 600;
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1.4;
  margin-top: 0.2rem;
`;

const XRefCite = styled.span`
  color: #8896ac;
  font-weight: 400;
`;

const XRefNote = styled.div`
  font-size: 0.6rem;
  color: #cbd5e1;
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1.4;
  margin-top: 0.2rem;
`;

const PulseBar = styled.div`
  height: 16px;
  border-radius: 4px;
  margin-bottom: 0.3rem;
  background: linear-gradient(
    90deg,
    rgba(52, 211, 153, 0.08) 0%,
    rgba(52, 211, 153, 0.2) 50%,
    rgba(52, 211, 153, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

// ─── helpers ────────────────────────────────────────────────────────────────────

const CYCLE_HOVER =
  'Cycle year is when the state’s Integrated Report (303(d)) was submitted — a regulatory milestone, not a data-freshness date. Underlying monitoring data may pre-date it.';

function fmtPct(n: number | null | undefined, decimals = 0): string {
  if (n == null) return '—';
  return `${n.toFixed(decimals)}%`;
}

// Aggregate the Cause-status impairments across the watershed's assessment units:
// distinct parameter -> how many units list it, and the uses it impairs.
function aggregateCauses(w: IAttainsWatershed): { name: string; units: number; uses: string[] }[] {
  const map = new Map<string, { units: number; uses: Set<string> }>();
  (w.assessmentUnits ?? []).forEach((au) => {
    au.causes.forEach((c) => {
      const entry = map.get(c.parameterName) ?? { units: 0, uses: new Set<string>() };
      entry.units += 1;
      c.associatedUses.forEach((u) => entry.uses.add(u));
      map.set(c.parameterName, entry);
    });
  });
  return Array.from(map.entries())
    .map(([name, v]) => ({ name, units: v.units, uses: Array.from(v.uses) }))
    .sort((a, b) => b.units - a.units);
}

// ─── component ────────────────────────────────────────────────────────────────

const ReceivingWaterStatusCard: React.FC = () => {
  const { attains, dischargeData, openAttainsDetail } = useFacilityModeData();
  const [crosswalk, setCrosswalk] = useState<Record<string, string[]> | null>(null);

  // Load the DMR→category crosswalk once (cached 90 days). Non-fatal on failure —
  // the cross-reference section simply won't render.
  useEffect(() => {
    let cancelled = false;
    fetchDmrCrosswalk()
      .then((m) => !cancelled && setCrosswalk(m))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Facility DMR parameters (distinct by code) across all outfalls.
  const facilityParams = useMemo<IFacilityParam[]>(() => {
    if (!dischargeData) return [];
    const seen = new Map<string, string>();
    dischargeData.forEach((f) =>
      f.outfalls.forEach((o) =>
        o.parameters.forEach((p) => {
          if (p.parameterCode && !seen.has(p.parameterCode)) {
            seen.set(p.parameterCode, p.parameterName);
          }
        })
      )
    );
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [dischargeData]);

  const w = attains.state === 'ok' ? (attains.data as IAttainsWatershed) : null;

  const xref = useMemo(() => {
    if (!w?.available || !crosswalk) return null;
    const groups = (w.impairmentCauses ?? [])
      .map((c) => c.parameterGroupName)
      .filter((g): g is string => !!g);
    if (groups.length === 0) return null; // watershed has no impairment causes to cross-reference
    return matchDischargeToImpairments(facilityParams, crosswalk, groups);
  }, [w, crosswalk, facilityParams]);

  if (attains.state === 'loading') {
    return (
      <Card>
        <Head>
          <SourcePill>EPA ATTAINS · Receiving water</SourcePill>
          <CyclePill>303(d)</CyclePill>
        </Head>
        <PulseBar />
        <Sub>Loading watershed assessment…</Sub>
      </Card>
    );
  }

  if (attains.state === 'error') {
    return (
      <Card $unavailable>
        <Head>
          <SourcePill>EPA ATTAINS · Receiving water</SourcePill>
          <CyclePill>unavailable</CyclePill>
        </Head>
        <Line>
          <Muted>Watershed assessment unavailable</Muted>
        </Line>
        <Sub>{attains.error || 'ATTAINS did not respond.'} · No fallback values shown.</Sub>
      </Card>
    );
  }

  if (!w?.available) {
    return (
      <Card $unavailable>
        <Head>
          <SourcePill>EPA ATTAINS · Receiving water</SourcePill>
          <CyclePill>n/a</CyclePill>
        </Head>
        <Line>
          <Muted>Receiving water: unresolved</Muted>
        </Line>
        <Sub>{w?.reason || 'No ATTAINS assessment for this watershed.'} · attains.epa.gov</Sub>
      </Card>
    );
  }

  const s = w.summary!;
  const causes = aggregateCauses(w);
  const cycleLabel = w.reportingCycles && w.reportingCycles.length > 0
    ? w.reportingCycles.join(' / ')
    : '—';

  return (
    <Card>
      <Head>
        <SourcePill>EPA ATTAINS · Receiving water</SourcePill>
        <HeadRight>
          <CyclePill title={CYCLE_HOVER}>303(d) cycle: {cycleLabel}</CyclePill>
          <MaxBtn
            onClick={openAttainsDetail}
            title="Open detailed watershed report"
            aria-label="Open detailed watershed report"
          >
            ⤢
          </MaxBtn>
        </HeadRight>
      </Head>

      {/* Line 1 — facts, no verdict */}
      <Line>
        <Muted>Watershed HUC-12 {w.huc12}</Muted> · {s.assessmentUnitCount} assessment units ·{' '}
        {fmtPct(s.assessedPercent)} of area assessed ·{' '}
        <Muted>{fmtPct(s.unassessedPercent)} unassessed</Muted>
      </Line>

      {/* Line 2 — impairment breakdown of the assessed portion */}
      <Line>
        Of assessed area: <Impaired>{s.impairedUnits} impaired</Impaired> (
        {fmtPct(s.impairedPercent, 1)} of watershed),{' '}
        <Supporting>{s.supportingUnits} fully supporting</Supporting> (
        {fmtPct(s.supportingPercent, 1)})
      </Line>

      {/* Line 3 — cause list with unit counts + impaired uses (authoritative, per-AU) */}
      {causes.length > 0 ? (
        causes.map((c) => (
          <CauseRow key={c.name}>
            • {c.name} <Muted>({c.units} unit{c.units !== 1 ? 's' : ''}</Muted>
            {c.uses.length > 0 && <CauseUses> · {c.uses.join(', ')}</CauseUses>}
            <Muted>)</Muted>
          </CauseRow>
        ))
      ) : (
        <CauseRow>
          <Muted>No impairment causes listed for assessed units.</Muted>
        </CauseRow>
      )}

      <Sub>
        Source: EPA ATTAINS · assessment-cycle data, not live. Cycle year = Integrated Report
        submission, not monitoring date.
      </Sub>

      {/* Q3 — discharge ↔ impairment cross-reference (deterministic, per EPA crosswalk) */}
      {xref && (
        <>
          <XRefHead>Discharge cross-reference</XRefHead>
          {xref.links.length > 0 ? (
            xref.links.map((l) => (
              <XRefLink key={`${l.parameterCode}-${l.attainsGroup}`}>
                {l.parameterName} ↔ {l.attainsGroup} impairment{' '}
                <XRefCite>(per EPA DMR pollutant-category crosswalk)</XRefCite>
              </XRefLink>
            ))
          ) : (
            <XRefNote>
              No facility DMR parameters map to any current watershed impairment cause per EPA’s
              DMR pollutant-category crosswalk. This does not mean the facility has no environmental
              impact — the current DMR does not directly report the pollutants driving these
              impairments.
            </XRefNote>
          )}
        </>
      )}
    </Card>
  );
};

export default ReceivingWaterStatusCard;
